import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  View,
} from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../v10/theme';
import type { ToolProps } from '../olmps/ToolHost';
import { registerDeityForm, unregisterDeityForm } from '../olmps/deityFormRegistry';

// ----- choice_tree JSON shape (matches web `JnusChoiceTreeData` /
// `JnusOptionData` in src/interfaces/settings.ts, stored verbatim in the
// `jnus_decisions.choice_tree` jsonb column) -----
interface JnusReward {
  id: string;
  description: string;
}

interface JnusOption {
  id: string;
  title: string;
  side: 'left' | 'right';
  rewards: JnusReward[];
  exposures: JnusReward[];
}

interface JnusChoiceTree {
  stepTitle: string;
  stepDescription: string;
  choiceTitle: string;
  options: JnusOption[];
  // Persisted Decision Matrix / Decision Slider state. Optional so pre-existing
  // rows (whose choice_tree predates these fields) still load; the widgets fall
  // back to DEFAULT_MATRIX_DATA / DEFAULT_SLIDER_DATA when absent, and only
  // write once the user actually interacts. (Interface types are hoisted, so
  // referencing the shapes declared lower in this file is fine.)
  matrix?: JnusMatrixData;
  slider?: JnusSliderData;
}

interface Decision {
  id: string;
  title: string;
  notes: string | null;
  decided: boolean;
  choice_tree: JnusChoiceTree | null;
  created_at: string;
  updated_at?: string | null;
}

const DEFAULT_CHOICE_TREE: JnusChoiceTree = {
  stepTitle: 'Situation',
  stepDescription: '',
  choiceTitle: 'Choice',
  options: [],
};

const generateId = () => `jn-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

function formatDate(ts: string | null | undefined): string {
  if (!ts) return '';
  const date = new Date(ts);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

type DetailPatch = Partial<Pick<Decision, 'title' | 'notes' | 'decided' | 'choice_tree'>>;

// ============================================================================
// Decision Slider — 8×8 Time × Reversibility matrix.
// Ported verbatim (labels + all 64 advice strings) from the web widget at
// src/app/jnus/decision-slider.tsx. The web version is a drag/slider pair
// over a CSS grid; this is the same grid rendered as 64 tappable cells
// (native has no drag-slider primitive), driving the same lookup tables.
// ============================================================================

const sliderXLabels = [
  'You have no time at all — this decision must be made right now.',
  "You're under heavy time pressure — you have minutes, not hours.",
  "You're pressed for time but can spare a brief moment to think.",
  'You have limited time — enough for a quick assessment but not deep analysis.',
  'You have moderate time — a day or two to sit with this before deciding.',
  'You have a comfortable window — enough time to gather input and weigh options.',
  'You have plenty of time — no deadline is forcing your hand anytime soon.',
  'There is no time pressure whatsoever — you can take as long as you need.',
];

const sliderYLabels = [
  'This decision is completely reversible — you can undo it with almost no cost.',
  "This is easily reversible — walking it back would take minimal effort.",
  "This is mostly reversible — you can undo it but there's a small cost or inconvenience.",
  'This is somewhat reversible — you could change course but it would take real effort.',
  'This is difficult to reverse — undoing it would cost you significant time, money, or energy.',
  'This is mostly irreversible — only parts of it can be walked back, and with real consequences.',
  'This is nearly irreversible — once done, almost nothing can be undone.',
  'This is completely irreversible — there is no going back once you commit.',
];

const sliderAdvice: string[][] = [
  ["Don't think — just go with your gut. You can always undo it and the hesitation is costing you more than any wrong choice would.", "Act on instinct. Your intuition has likely already picked up on the right move. If it doesn't work out, it's a quick fix.", "Go with your first instinct. There's a small cost to reversing it, but waiting around right now is worse. Trust the pattern your mind has already seen.", 'You have enough time for a quick assessment. Check your gut, confirm it makes sense logically, and act. You can always change your mind with zero consequences.', "You have a day or two and this is fully undoable. Think it through but don't let it consume you. Make a decision, try it out, and adjust as needed.", "You have plenty of room and zero consequences for being wrong. This is your playground. Pick the option with the highest possible reward. Ask yourself what you'd regret not trying.", "You have all the time in the world and can undo anything. This is the time to be bold. Pick the option with the absolute highest reward. What would you regret not doing? Do that.", 'Maximum freedom. Pick the option with the most reward, even if it seems risky. You have unlimited time and you can undo anything. What would you regret never trying? Do that.'],
  ["Act on instinct. Your intuition has probably detected information your conscious mind hasn't processed yet. Low downside to acting quickly.", "Trust your instincts and act. You've probably already sensed the right answer — your conscious mind just hasn't caught up yet. Easy to walk back if needed.", "Go with your gut but take 30 seconds to mentally check: does this feel off? If not, move. The small cost of reversing is worth avoiding paralysis right now.", "Take a few minutes to think it through, but don't drag it out. Your intuition plus a brief sanity check is plenty here. Reversing is painless if you're wrong.", "Take a day to sit with it. Sleep on it if you can. But don't agonize — the cost of being wrong is almost nothing. Go with the option that excites you most.", 'You have time and freedom to experiment. Go for the bold option — the one that excites you. If it doesn\'t work, walking it back is simple. This is the time to swing.', "No rush, no risk. Go for the option that excites you most — the biggest swing. If it fails, you can walk it back easily and you'll have learned something valuable.", "Dream big, pick the boldest option, and go. If it doesn't work, you'll reverse it effortlessly and try again. There is almost no downside here — only upside."],
  ["Go with your first instinct. There's a small cost to reversing, but waiting is worse. Trust the pattern your mind has already seen.", 'Go with your gut but take 30 seconds to mentally check: does this feel off? If not, move. The cost of reversing is worth avoiding paralysis.', 'You have just enough time to think through your top two options. Pick the one that feels right and move. The reversal cost is small enough to justify speed.', "You have time to compare a couple of options side by side. Pick the one that feels strongest and move. If it's wrong, the cost of switching is manageable.", 'Compare your top choices across the things that matter most to you. Lean toward the higher-reward option since you can mostly undo this.', "Take your time to evaluate, but lean toward the higher-reward option. The cost of reversing is small, so don't let fear hold you back from the bigger gain.", "You're in the sweet spot for experimentation. Take the high-reward option. Even if it doesn't pan out, the cost of adjusting is minor and you'll gain experience.", "Nearly zero risk. Take the ambitious path. Go for the highest reward available. If it doesn't work, the adjustment cost is small and you'll walk away with knowledge."],
  ["Go with your gut but pick the option that's easiest to adjust later. You don't have time to analyze, but you can steer after the fact.", 'You need to act soon. Pick the option that gives you the most flexibility after the fact. You may not get this perfectly right, but you want room to adjust.', 'Take five minutes to compare your top two or three options. Lean toward the one that gives you the most room to adjust later, since reversing takes real effort.', 'List your top three options. Briefly weigh pros and cons, then go with the one that balances reward and flexibility. You want room to course-correct.', 'Good position. Take a day to list your options and evaluate them on the criteria that matter. Pick the best risk-reward balance, knowing you can adjust later.', 'Do a proper evaluation — list your options, weigh what matters, and pick the one with the strongest upside. Just make sure the downside is manageable.', 'You can afford to be ambitious. Evaluate your options, pick the one with the strongest upside, and go for it. If you need to adjust later, you can — it just takes work.', 'Be bold in your choice but smart in your preparation. Go for the high-reward option with a backup plan in place.'],
  ["This is a pressure moment. Pick the safest option that keeps you in the game. You can't afford to swing big here — protect your position.", "You're under pressure and this sticks. Eliminate the worst option, then choose the safest of what's left. Don't chase reward here.", "You don't have long, but this matters. Quickly list your options, eliminate the riskiest one, and go with the safest remaining choice.", "Take the time you have seriously. Write out your options, identify the one with the best risk-reward ratio, and commit. Don't chase the flashiest option.", "Don't rush, but don't stall. This is hard to undo. List three to four options, evaluate risk and reward, and pick the one where the worst case is something you can handle.", 'Be thorough. List your options, define your criteria, and do a real comparison. Pick the option with the best risk-reward ratio — not the safest, not the flashiest.', 'You have time, so use it for proper analysis. This is hard to undo. List your options, compare risk and reward carefully, and choose the best overall ratio.', 'Do comprehensive analysis. List every option, evaluate each one rigorously, and choose the one with the strongest risk-reward ratio.'],
  ["You're in damage-control mode. Choose the least risky path available right now. Survival first — there will be time to optimize later, but only if you don't blow it here.", "This is serious. Use whatever minutes you have to identify the option with the lowest downside. This isn't about winning — it's about not losing badly.", "Slow down as much as you can afford to. Identify the option with the best downside protection. You're not looking for the biggest win — you're avoiding an unrecoverable loss.", 'Use every bit of time available. List three or four options, score them on risk and reward, and pick the one where the worst-case is still acceptable. This is mostly permanent.', "Do proper analysis — list options, define what matters, and score them. The best risk-reward ratio should guide you, not excitement or fear. This mostly can't be undone.", 'Do full analysis. List options, evaluate criteria, consider worst-case scenarios. Talk to someone with experience if you can. Choose the best risk-reward ratio.', 'Be thorough and deliberate. This is mostly permanent. Do a complete analysis — options, criteria, risk, reward, worst-case scenarios. Get outside perspective if possible.', 'Do the deepest analysis you can. Research, consult, list options, evaluate criteria, run worst-case scenarios. Only commit when you feel genuinely confident.'],
  ['High-stakes snap decision. Default to safety. Pick the option with the smallest downside, even if it means leaving reward on the table.', "High pressure, high stakes. Strip it down to two options and pick the one you'd regret less if it went wrong. Just protect yourself.", 'Use every minute you have. Write down two or three options, cross off anything with a catastrophic downside, and choose the most conservative remaining path.', "Be methodical with the time you have. Eliminate high-risk options first, then choose the safest path among what's left. Don't gamble here.", 'You have time and you need to use it well. This is nearly permanent. Gather input, list your options, and analyze the downside of each thoroughly before committing.', "Be rigorous. List three to four options, evaluate each on risk and reward, pressure-test your assumptions, and only commit when you're confident in your analysis.", "Research thoroughly, consult trusted advisors, list every option, and analyze risk-reward exhaustively. Don't commit until you've stress-tested your choice.", "Use every advantage time gives you. Be exhaustive in your research, talk to people who've made similar decisions, and analyze every option from every angle."],
  ["Choose the option you could most live with if everything else went wrong. Minimize regret, not risk-reward. Protect what matters most.", 'The worst-case scenario must guide you. Forget upside — which option has the most survivable downside? Choose that and accept you did the best you could.', "This cannot be undone and you're short on time. Focus on which option you could live with permanently. If you can buy more time, do it. If not, choose safety.", "Use all the time you've got. Focus on the downside of each option. Pick the one where failure is most survivable. This decision is forever.", 'This is permanent. Be thorough. List every viable option, evaluate risk and reward carefully, consult someone you trust if possible, and choose the best risk-reward ratio.', "This is permanent and you have the luxury of time — don't waste it. Do deep analysis. Gather data, consult people you trust, evaluate risk and reward methodically.", "This is where you do your life's most careful thinking. Exhaust every option, consult every source you trust, analyze every risk and reward. Give this decision everything it deserves.", 'This is permanent and you have unlimited time. Be exhaustive — research every option, consult every trusted source, analyze every angle. Only commit when you can stand behind this choice permanently.'],
];

const makeSliderQuadrantColors = (t: { onPace: string }): string[][] => [
  [t.onPace, t.onPace, t.onPace, t.onPace, '#2563eb', '#2563eb', '#2563eb', '#2563eb'],
  [t.onPace, t.onPace, t.onPace, t.onPace, '#2563eb', '#2563eb', '#2563eb', '#2563eb'],
  [t.onPace, t.onPace, t.onPace, t.onPace, '#2563eb', '#2563eb', '#2563eb', '#2563eb'],
  [t.onPace, t.onPace, t.onPace, t.onPace, '#2563eb', '#2563eb', '#2563eb', '#2563eb'],
  ['#d97706', '#d97706', '#d97706', '#d97706', '#7c3aed', '#7c3aed', '#7c3aed', '#7c3aed'],
  ['#d97706', '#d97706', '#d97706', '#d97706', '#7c3aed', '#7c3aed', '#7c3aed', '#7c3aed'],
  ['#d97706', '#d97706', '#d97706', '#d97706', '#7c3aed', '#7c3aed', '#7c3aed', '#7c3aed'],
  ['#d97706', '#d97706', '#d97706', '#d97706', '#7c3aed', '#7c3aed', '#7c3aed', '#7c3aed'],
];

const sliderQuadrantNames: string[][] = [
  ['INSTINCT', 'INSTINCT', 'INSTINCT', 'INSTINCT', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD'],
  ['INSTINCT', 'INSTINCT', 'INSTINCT', 'INSTINCT', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD'],
  ['INSTINCT', 'INSTINCT', 'INSTINCT', 'INSTINCT', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD'],
  ['INSTINCT', 'INSTINCT', 'INSTINCT', 'INSTINCT', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD', 'SWING FOR REWARD'],
  ['LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD'],
  ['LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD'],
  ['LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD'],
  ['LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'LEAST RISKY', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD', 'ANALYZE RISK-REWARD'],
];

const SLIDER_CELL = 28;
const SLIDER_GRID = SLIDER_CELL * 8;
const GRID_INDICES = [0, 1, 2, 3, 4, 5, 6, 7] as const;

// Grid position (Time × Reversibility), persisted into choice_tree.slider.
interface JnusSliderData {
  xVal: number;
  yVal: number;
}

const DEFAULT_SLIDER_DATA: JnusSliderData = { xVal: 0, yVal: 0 };

// Controlled by the parent's choice_tree.slider so the picked cell survives a
// reload and counts as a measurable action for the deity "Principles" system.
// Still `key`-mounted per selected decision in the detail view below.
function DecisionSliderWidget({
  value,
  onChange,
}: {
  value: JnusSliderData;
  onChange: (next: JnusSliderData) => void;
}) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const xVal = value.xVal;
  const yVal = value.yVal;

  const activeColor = makeSliderQuadrantColors(t)[yVal][xVal];
  const activeQuadrant = sliderQuadrantNames[yVal][xVal];
  const adviceText = sliderAdvice[yVal][xVal];

  return (
    <View style={styles.widgetCard}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <Text style={styles.widgetTitle}>Decision Matrix</Text>
      <Text style={styles.widgetSubtitle}>Time × Reversibility</Text>

      <View
        style={[
          styles.quadrantBadge,
          { backgroundColor: activeColor + '15', borderColor: activeColor + '44' },
        ]}
      >
        <Text style={[styles.quadrantBadgeText, { color: activeColor }]}>{activeQuadrant}</Text>
      </View>

      <Text style={styles.sliderAxisLabel}>Irreversible ↑</Text>
      <View
        style={[styles.sliderGrid, { width: SLIDER_GRID, height: SLIDER_GRID }]}
      >
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
        {GRID_INDICES.map((row) => (
          <View key={row} style={styles.sliderRow}>
            {GRID_INDICES.map((col) => {
              const y = 7 - row;
              const x = col;
              const isActive = x === xVal && y === yVal;
              const qColor = makeSliderQuadrantColors(t)[y][x];
              return (
                <Pressable
                  key={col}
                  onPress={() => onChange({ xVal: x, yVal: y })}
                  style={[
                    styles.sliderCell,
                    {
                      backgroundColor: isActive ? qColor + '55' : qColor + '12',
                      borderRightWidth: col < 7 ? StyleSheet.hairlineWidth : 0,
                      borderBottomWidth: row < 7 ? StyleSheet.hairlineWidth : 0,
                      borderColor: c.glassRim,
                    },
                  ]}
                >
                  {isActive ? (
                    <View style={[styles.sliderDot, { backgroundColor: qColor }]} />
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        ))}
      </View>
      <Text style={styles.sliderAxisLabel}>↓ Reversible</Text>

      <View style={[styles.sliderXAxisRow, { width: SLIDER_GRID }]}>
        <Text style={styles.sliderAxisLabelSmall}>← No Time</Text>
        <Text style={styles.sliderAxisLabelSmall}>Plenty of Time →</Text>
      </View>

      <View style={styles.widgetBox}>
        <Text style={[styles.widgetBoxLabel, { color: '#d97706' }]}>TIME</Text>
        <Text style={styles.widgetBoxText}>{sliderXLabels[xVal]}</Text>
      </View>
      <View style={styles.widgetBox}>
        <Text style={[styles.widgetBoxLabel, { color: '#7c3aed' }]}>REVERSIBILITY</Text>
        <Text style={styles.widgetBoxText}>{sliderYLabels[yVal]}</Text>
      </View>
      <View
        style={[
          styles.widgetBox,
          { backgroundColor: activeColor + '08', borderColor: activeColor + '30' },
        ]}
      >
        <Text style={[styles.widgetBoxLabel, { color: activeColor }]}>ADVICE</Text>
        <Text style={styles.widgetBoxText}>{adviceText}</Text>
      </View>
    </View>
  );
}

// ============================================================================
// Decision Matrix Table — weighted criteria × options comparison.
// Ported verbatim (weight-rebalancing math, add/remove semantics, winner
// logic) from the web widget at src/app/jnus/decision-matrix-table.tsx.
// ============================================================================

interface MatrixCriterion {
  name: string;
  weight: number;
}

const defaultMatrixCriteria: MatrixCriterion[] = [
  { name: 'Cost', weight: 3 },
  { name: 'Quality', weight: 3 },
  { name: 'Time', weight: 2 },
  { name: 'Risk', weight: 2 },
];

const defaultMatrixOptions = ['Option A', 'Option B', 'Option C'];

// Full editable state of the Weighted Decision Matrix, persisted into
// choice_tree.matrix.
interface JnusMatrixData {
  locked: boolean;
  lockedTotal: number;
  criteria: MatrixCriterion[];
  options: string[];
  scores: number[][];
}

const DEFAULT_MATRIX_DATA: JnusMatrixData = {
  locked: true,
  lockedTotal: 10,
  criteria: defaultMatrixCriteria,
  options: defaultMatrixOptions,
  scores: defaultMatrixCriteria.map(() => defaultMatrixOptions.map(() => 3)),
};

const MATRIX_COL_CRITERIA = 120;
const MATRIX_COL_WT = 52;
const MATRIX_COL_OPTION = 92;

// Controlled by the parent's choice_tree.matrix. Every mutator computes the
// full next state and pushes it up through `onChange` (→ updateTree →
// debounced queueSave), so edits persist and count as measurable actions for
// the deity "Principles" system. The weight-rebalancing / winner math is a
// verbatim port of the web widget; only the state plumbing changed from local
// useState to lifted value/onChange.
function DecisionMatrixWidget({
  value,
  onChange,
}: {
  value: JnusMatrixData;
  onChange: (next: JnusMatrixData) => void;
}) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const { locked, lockedTotal, criteria, options, scores } = value;
  const emit = (patch: Partial<JnusMatrixData>) => onChange({ ...value, ...patch });

  const totalWeight = criteria.reduce((s, c) => s + c.weight, 0);

  // Verbatim port of the web widget's `updateWeight`.
  const updateWeight = (idx: number, val: string) => {
    const num = Math.max(1, Math.min(99, Number(val) || 1));
    const next = [...criteria];

    if (locked) {
      const old = next[idx].weight;
      const diff = num - old;
      const others = next.filter((_, i) => i !== idx);
      const othersTotal = others.reduce((s, c) => s + c.weight, 0);
      const available = othersTotal - others.length;

      if (diff > available) return;
      if (num > lockedTotal - (next.length - 1)) return;

      next[idx] = { ...next[idx], weight: num };

      let remaining = lockedTotal - num;
      const otherIndices = next.map((_, i) => i).filter((i) => i !== idx);
      const otherWeights = otherIndices.map((i) => next[i].weight);
      const otherSum = otherWeights.reduce((a, b) => a + b, 0);

      otherIndices.forEach((oi, j) => {
        if (j === otherIndices.length - 1) {
          next[oi] = { ...next[oi], weight: Math.max(1, remaining) };
        } else {
          const proportion = otherSum > 0 ? otherWeights[j] / otherSum : 1 / otherIndices.length;
          const newW = Math.max(1, Math.round(remaining * proportion));
          next[oi] = { ...next[oi], weight: newW };
          remaining -= newW;
        }
      });
    } else {
      next[idx] = { ...next[idx], weight: num };
    }

    emit({ criteria: next });
  };

  const updateScore = (ci: number, oi: number, val: string) => {
    const num = Math.max(1, Math.min(10, Number(val) || 1));
    const next = scores.map((row, i) => (i === ci ? row.map((s, j) => (j === oi ? num : s)) : [...row]));
    emit({ scores: next });
  };

  const updateCriteriaName = (idx: number, name: string) => {
    const next = [...criteria];
    next[idx] = { ...next[idx], name };
    emit({ criteria: next });
  };

  const updateOptionName = (idx: number, name: string) => {
    const next = [...options];
    next[idx] = name;
    emit({ options: next });
  };

  const addCriteria = () => {
    let nextCriteria: MatrixCriterion[];
    if (locked) {
      if (criteria.length >= lockedTotal) return;
      const next = [...criteria];
      const maxIdx = next.reduce((mi, c, i) => (c.weight > next[mi].weight ? i : mi), 0);
      if (next[maxIdx].weight <= 1) return;
      next[maxIdx] = { ...next[maxIdx], weight: next[maxIdx].weight - 1 };
      nextCriteria = [...next, { name: 'New', weight: 1 }];
    } else {
      nextCriteria = [...criteria, { name: 'New', weight: 3 }];
    }
    emit({ criteria: nextCriteria, scores: [...scores, options.map(() => 3)] });
  };

  const removeCriteria = (idx: number) => {
    if (criteria.length <= 1) return;
    const removed = criteria[idx].weight;
    const next = criteria.filter((_, i) => i !== idx);
    if (locked) {
      next[0] = { ...next[0], weight: next[0].weight + removed };
    }
    emit({ criteria: next, scores: scores.filter((_, i) => i !== idx) });
  };

  const addOption = () => {
    emit({
      options: [...options, 'Option ' + String.fromCharCode(65 + options.length)],
      scores: scores.map((row) => [...row, 3]),
    });
  };

  const removeOption = (idx: number) => {
    if (options.length <= 2) return;
    emit({
      options: options.filter((_, i) => i !== idx),
      scores: scores.map((row) => row.filter((_, i) => i !== idx)),
    });
  };

  const updateLockedTotal = (val: string) => {
    const v = Math.max(criteria.length, Number(val) || criteria.length);
    const next = [...criteria];
    let remaining = v;
    next.forEach((c, i) => {
      if (i === next.length - 1) {
        next[i] = { ...c, weight: Math.max(1, remaining) };
      } else {
        const share = Math.max(1, Math.round(v / next.length));
        next[i] = { ...c, weight: share };
        remaining -= share;
      }
    });
    emit({ lockedTotal: v, criteria: next });
  };

  const totals = options.map((_, oi) => criteria.reduce((sum, c, ci) => sum + c.weight * scores[ci][oi], 0));
  const maxTotal = Math.max(...totals);
  const winnerIdx = totals.indexOf(maxTotal);
  const isTie = totals.filter((t) => t === maxTotal).length > 1;

  return (
    <View style={styles.widgetCard}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <Text style={styles.widgetTitle}>Weighted Decision Matrix</Text>

      <View style={styles.matrixControlsRow}>
        <View style={styles.matrixToggleGroup}>
          <Text style={styles.matrixControlLabel}>Weights:</Text>
          <Pressable
            onPress={() => emit({ locked: false })}
            style={[styles.matrixToggleBtn, !locked && { backgroundColor: t.gold, borderColor: t.gold }]}
          >
            <Text style={[styles.matrixToggleBtnText, !locked && styles.matrixToggleBtnTextActive]}>Flexible</Text>
          </Pressable>
          <Pressable
            onPress={() => emit({ locked: true })}
            style={[styles.matrixToggleBtn, locked && { backgroundColor: t.gold, borderColor: t.gold }]}
          >
            <Text style={[styles.matrixToggleBtnText, locked && styles.matrixToggleBtnTextActive]}>Locked</Text>
          </Pressable>
        </View>

        {locked ? (
          <View style={styles.matrixBudgetGroup}>
            <Text style={styles.matrixControlLabel}>Budget:</Text>
            <TextInput
              value={String(lockedTotal)}
              onChangeText={updateLockedTotal}
              keyboardType="number-pad"
              style={styles.matrixBudgetInput}
            />
          </View>
        ) : null}

        <Text
          style={[
            styles.matrixTotalText,
            { color: locked ? (totalWeight === lockedTotal ? t.onPace : t.lapsed) : c.slate },
          ]}
        >
          {locked ? `${totalWeight} / ${lockedTotal}` : `Total: ${totalWeight}`}
        </Text>
      </View>

      <View style={styles.matrixActionsRow}>
        <Pressable onPress={addCriteria} style={styles.matrixActionBtn}>
          <Text style={styles.matrixActionText}>+ Criteria</Text>
        </Pressable>
        <Pressable onPress={addOption} style={styles.matrixActionBtn}>
          <Text style={styles.matrixActionText}>+ Option</Text>
        </Pressable>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator style={styles.matrixTableScroll}>
        <View>
          {/* Header row */}
          <View style={styles.matrixRow}>
            <View style={[styles.matrixCell, { width: MATRIX_COL_CRITERIA, alignItems: 'flex-start' }]}>
              <Text style={styles.matrixHeaderText}>Criteria</Text>
            </View>
            <View style={[styles.matrixCell, { width: MATRIX_COL_WT }]}>
              <Text style={[styles.matrixHeaderText, { color: t.goldBright }]}>Wt</Text>
            </View>
            {options.map((opt, oi) => (
              <View
                key={oi}
                style={[
                  styles.matrixCell,
                  styles.matrixCellBordered,
                  { width: MATRIX_COL_OPTION },
                  !isTie && oi === winnerIdx ? styles.matrixWinnerBg : null,
                ]}
              >
                <TextInput
                  value={opt}
                  onChangeText={(t) => updateOptionName(oi, t)}
                  style={[
                    styles.matrixOptionNameInput,
                    !isTie && oi === winnerIdx ? { color: t.onPace } : null,
                  ]}
                />
                {options.length > 2 ? (
                  <Pressable onPress={() => removeOption(oi)} style={styles.matrixRemoveBtn} hitSlop={6}>
                    <Text style={styles.matrixRemoveText}>×</Text>
                  </Pressable>
                ) : null}
              </View>
            ))}
          </View>

          {/* Body rows */}
          {criteria.map((c, ci) => (
            <View key={ci} style={styles.matrixRow}>
              <View style={[styles.matrixCell, { width: MATRIX_COL_CRITERIA, alignItems: 'flex-start' }]}>
                <View style={styles.matrixCriteriaNameRow}>
                  <TextInput
                    value={c.name}
                    onChangeText={(t) => updateCriteriaName(ci, t)}
                    style={styles.matrixCriteriaNameInput}
                  />
                  {criteria.length > 1 ? (
                    <Pressable onPress={() => removeCriteria(ci)} style={styles.matrixRemoveBtn} hitSlop={6}>
                      <Text style={styles.matrixRemoveText}>×</Text>
                    </Pressable>
                  ) : null}
                </View>
              </View>
              <View style={[styles.matrixCell, { width: MATRIX_COL_WT }]}>
                <TextInput
                  value={String(c.weight)}
                  onChangeText={(t) => updateWeight(ci, t)}
                  keyboardType="number-pad"
                  style={styles.matrixWeightInput}
                />
              </View>
              {options.map((_, oi) => {
                const weighted = c.weight * scores[ci][oi];
                return (
                  <View
                    key={oi}
                    style={[
                      styles.matrixCell,
                      styles.matrixCellBordered,
                      { width: MATRIX_COL_OPTION },
                      !isTie && oi === winnerIdx ? styles.matrixWinnerBgSoft : null,
                    ]}
                  >
                    <TextInput
                      value={String(scores[ci][oi])}
                      onChangeText={(t) => updateScore(ci, oi, t)}
                      keyboardType="number-pad"
                      style={styles.matrixScoreInput}
                    />
                    <Text style={styles.matrixWeightedText}>({weighted})</Text>
                  </View>
                );
              })}
            </View>
          ))}

          {/* Footer row: totals + winner */}
          <View style={[styles.matrixRow, styles.matrixFooterRow]}>
            <View style={[styles.matrixCell, { width: MATRIX_COL_CRITERIA, alignItems: 'flex-start' }]}>
              <Text style={styles.matrixTotalLabel}>Total</Text>
            </View>
            <View style={[styles.matrixCell, { width: MATRIX_COL_WT }]} />
            {totals.map((total, oi) => (
              <View
                key={oi}
                style={[
                  styles.matrixCell,
                  styles.matrixCellBordered,
                  { width: MATRIX_COL_OPTION },
                  !isTie && oi === winnerIdx ? styles.matrixWinnerBg : null,
                ]}
              >
                <Text style={[styles.matrixTotalValue, !isTie && oi === winnerIdx ? { color: t.onPace } : null]}>
                  {total}
                </Text>
                {!isTie && oi === winnerIdx ? <Text style={styles.matrixWinnerLabel}>★ WINNER</Text> : null}
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

export function JnusTool(_props: ToolProps) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Section toggles, mirroring the web detail page's four `Switch` toggles
  // (Decision Slider / Decision Matrix / Choice S.C.O.R.E / Notes). Reset to
  // visible whenever a (new) decision is opened, matching the web page
  // component remounting fresh on each decisionId.
  const [showSlider, setShowSlider] = useState(true);
  const [showMatrix, setShowMatrix] = useState(true);
  const [showChoiceTree, setShowChoiceTree] = useState(true);
  const [showNotes, setShowNotes] = useState(true);

  useEffect(() => {
    setShowSlider(true);
    setShowMatrix(true);
    setShowChoiceTree(true);
    setShowNotes(true);
  }, [selectedId]);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    const { data, error: err } = await supabase
      .from('jnus_decisions')
      .select('id, title, notes, decided, choice_tree, created_at, updated_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (err) {
      setError(err.message);
    } else {
      setError(null);
      setDecisions((data ?? []) as Decision[]);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const createDecision = async () => {
    if (!userId || busy) return;
    setBusy(true);
    const { data, error: err } = await supabase
      .from('jnus_decisions')
      .insert({
        user_id: userId,
        title: 'New Situation',
        notes: '',
        decided: false,
        choice_tree: DEFAULT_CHOICE_TREE,
      })
      .select('id, title, notes, decided, choice_tree, created_at, updated_at')
      .single();
    if (err || !data) {
      setError(err?.message ?? 'Could not create situation.');
    } else {
      const created = data as Decision;
      setDecisions((prev) => [created, ...prev]);
      setSelectedId(created.id);
    }
    setBusy(false);
  };

  // Immediate (non-debounced) toggle, mirrors the web list's `toggleDecided`.
  const toggleDecided = async (d: Decision) => {
    const next = !d.decided;
    setDecisions((prev) => prev.map((x) => (x.id === d.id ? { ...x, decided: next } : x)));
    const { error: err } = await supabase
      .from('jnus_decisions')
      .update({ decided: next, updated_at: new Date().toISOString() })
      .eq('id', d.id)
      .eq('user_id', userId!);
    if (err) {
      setError(err.message);
      await load();
    }
  };

  const confirmDelete = (d: Decision) => {
    Alert.alert('Delete situation?', d.title || 'Untitled', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setDecisions((prev) => prev.filter((x) => x.id !== d.id));
          if (selectedId === d.id) setSelectedId(null);
          const { error: err } = await supabase
            .from('jnus_decisions')
            .delete()
            .eq('id', d.id)
            .eq('user_id', userId!);
          if (err) {
            setError(err.message);
            await load();
          }
        },
      },
    ]);
  };

  // Card long-press → the mobile stand-in for the web card's right-click
  // context menu (Open / Mark as Decided / Delete).
  const showCardMenu = (d: Decision) => {
    Alert.alert(d.title || 'Untitled', undefined, [
      { text: 'Open', onPress: () => setSelectedId(d.id) },
      {
        text: d.decided ? 'Move to Active' : 'Mark as Decided',
        onPress: () => void toggleDecided(d),
      },
      { text: 'Delete', style: 'destructive', onPress: () => confirmDelete(d) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // Debounced write, mirrors the web detail page's 800ms `saveDecision`.
  const queueSave = useCallback(
    (id: string, patch: DetailPatch) => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        const { error: err } = await supabase
          .from('jnus_decisions')
          .update({ ...patch, updated_at: new Date().toISOString() })
          .eq('id', id)
          .eq('user_id', userId!);
        if (err) setError(err.message);
      }, 800);
    },
    [userId]
  );

  const selectedDecision = decisions.find((d) => d.id === selectedId) ?? null;

  const updateDetail = useCallback(
    (patch: DetailPatch) => {
      if (!selectedId) return;
      setDecisions((prev) => prev.map((d) => (d.id === selectedId ? { ...d, ...patch } : d)));
      queueSave(selectedId, patch);
    },
    [selectedId, queueSave]
  );

  // Deity form-fill adapter (docs/DEITY_AGENT_BACKEND_DESIGN.md §4/§7): only
  // registered while a decision is open (selectedId set); applying routes
  // through updateDetail, the same debounced queueSave path every manual
  // detail edit uses. recordId (if present) must match the open decision.
  useEffect(() => {
    registerDeityForm('jnus_decision', {
      getCurrentValues: () => (selectedDecision ? (selectedDecision as unknown as Record<string, unknown>) : undefined),
      applyFill: (fields, recordId) => {
        if (!selectedId || (recordId && recordId !== selectedId)) return;
        updateDetail(fields as DetailPatch);
      },
    });
    return () => unregisterDeityForm('jnus_decision');
  }, [selectedId, selectedDecision, updateDetail]);

  const updateTree = useCallback(
    (updater: (tree: JnusChoiceTree) => JnusChoiceTree) => {
      const tree = selectedDecision?.choice_tree ?? DEFAULT_CHOICE_TREE;
      updateDetail({ choice_tree: updater(tree) });
    },
    [selectedDecision, updateDetail]
  );

  // Persist Decision Matrix / Decision Slider state into choice_tree so it
  // survives reloads and feeds the deity "Principles" system. Routed through
  // updateTree → updateDetail → debounced queueSave like every other edit.
  const updateMatrix = useCallback(
    (next: JnusMatrixData) => updateTree((tree) => ({ ...tree, matrix: next })),
    [updateTree]
  );

  const updateSlider = useCallback(
    (next: JnusSliderData) => updateTree((tree) => ({ ...tree, slider: next })),
    [updateTree]
  );

  const addOption = () => {
    updateTree((tree) => {
      const left = tree.options.filter((o) => o.side === 'left').length;
      const right = tree.options.filter((o) => o.side === 'right').length;
      const option: JnusOption = {
        id: generateId(),
        title: `Option ${tree.options.length + 1}`,
        side: left <= right ? 'left' : 'right',
        rewards: [],
        exposures: [],
      };
      return { ...tree, options: [...tree.options, option] };
    });
  };

  const updateOptionTitle = (optionId: string, title: string) => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) => (o.id === optionId ? { ...o, title } : o)),
    }));
  };

  const removeOption = (optionId: string) => {
    updateTree((tree) => ({ ...tree, options: tree.options.filter((o) => o.id !== optionId) }));
  };

  const moveOption = (optionId: string, side: 'left' | 'right') => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) => (o.id === optionId ? { ...o, side } : o)),
    }));
  };

  const addReward = (optionId: string) => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) =>
        o.id === optionId
          ? { ...o, rewards: [...o.rewards, { id: generateId(), description: '' }] }
          : o
      ),
    }));
  };

  const updateReward = (optionId: string, rewardId: string, description: string) => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) =>
        o.id === optionId
          ? { ...o, rewards: o.rewards.map((r) => (r.id === rewardId ? { ...r, description } : r)) }
          : o
      ),
    }));
  };

  const removeReward = (optionId: string, rewardId: string) => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) =>
        o.id === optionId ? { ...o, rewards: o.rewards.filter((r) => r.id !== rewardId) } : o
      ),
    }));
  };

  const addExposure = (optionId: string) => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) =>
        o.id === optionId
          ? { ...o, exposures: [...o.exposures, { id: generateId(), description: '' }] }
          : o
      ),
    }));
  };

  const updateExposure = (optionId: string, exposureId: string, description: string) => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) =>
        o.id === optionId
          ? {
              ...o,
              exposures: o.exposures.map((e) => (e.id === exposureId ? { ...e, description } : e)),
            }
          : o
      ),
    }));
  };

  const removeExposure = (optionId: string, exposureId: string) => {
    updateTree((tree) => ({
      ...tree,
      options: tree.options.map((o) =>
        o.id === optionId ? { ...o, exposures: o.exposures.filter((e) => e.id !== exposureId) } : o
      ),
    }));
  };

  // Option "⋯" menu → the mobile stand-in for the web option's right-click
  // context menu (Add Reward / Add Exposure / Move side / Delete Option).
  const showOptionMenu = (option: JnusOption) => {
    Alert.alert(option.title || 'Option', undefined, [
      { text: 'Add Reward', onPress: () => addReward(option.id) },
      { text: 'Add Exposure', onPress: () => addExposure(option.id) },
      {
        text: `Move to ${option.side === 'left' ? 'Right' : 'Left'}`,
        onPress: () => moveOption(option.id, option.side === 'left' ? 'right' : 'left'),
      },
      { text: 'Delete Option', style: 'destructive', onPress: () => removeOption(option.id) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const renderOption = (option: JnusOption) => (
    <View key={option.id} style={styles.optionCard}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <View style={styles.optionHeaderRow}>
        <TextInput
          style={styles.optionTitleInput}
          value={option.title}
          onChangeText={(t) => updateOptionTitle(option.id, t)}
          placeholder="Option"
          placeholderTextColor={c.slate}
        />
        <Pressable style={styles.optionMenuButton} onPress={() => showOptionMenu(option)}>
          <Text style={styles.optionMenuText}>⋯</Text>
        </Pressable>
      </View>

      {option.rewards.map((r) => (
        <View key={r.id} style={styles.tagRow}>
          <Text style={[styles.tag, styles.tagReward]}>Reward</Text>
          <TextInput
            style={styles.tagInput}
            value={r.description}
            onChangeText={(t) => updateReward(option.id, r.id, t)}
            placeholder="Description"
            placeholderTextColor={c.slate}
          />
          <Pressable onPress={() => removeReward(option.id, r.id)} hitSlop={8}>
            <Text style={styles.tagRemove}>×</Text>
          </Pressable>
        </View>
      ))}

      {option.exposures.map((ex) => (
        <View key={ex.id} style={styles.tagRow}>
          <Text style={[styles.tag, styles.tagExposure]}>Exposure</Text>
          <TextInput
            style={styles.tagInput}
            value={ex.description}
            onChangeText={(t) => updateExposure(option.id, ex.id, t)}
            placeholder="Description"
            placeholderTextColor={c.slate}
          />
          <Pressable onPress={() => removeExposure(option.id, ex.id)} hitSlop={8}>
            <Text style={styles.tagRemove}>×</Text>
          </Pressable>
        </View>
      ))}
    </View>
  );

  const renderCard = (d: Decision) => (
    <Pressable
      key={d.id}
      style={styles.card}
      onPress={() => setSelectedId(d.id)}
      onLongPress={() => showCardMenu(d)}
    >
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <View style={styles.cardTopRow}>
        <Text style={styles.cardTitle} numberOfLines={1}>
          {d.title || 'Untitled'}
        </Text>
        {d.decided ? (
          <View style={styles.decidedBadge}>
            <Text style={styles.decidedBadgeText}>Decided</Text>
          </View>
        ) : null}
      </View>
      <Text style={styles.cardDate}>{formatDate(d.created_at)}</Text>
      {d.notes ? (
        <Text style={styles.cardNotes} numberOfLines={1}>
          {d.notes}
        </Text>
      ) : null}
    </Pressable>
  );

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={t.goldBright} />
      </View>
    );
  }

  // ----- Detail view: title, decided toggle, notes, choice tree editor -----
  if (selectedDecision) {
    const tree = selectedDecision.choice_tree ?? DEFAULT_CHOICE_TREE;
    const leftOptions = tree.options.filter((o) => o.side === 'left');
    const rightOptions = tree.options.filter((o) => o.side === 'right');

    return (
      <ScrollView style={styles.root} contentContainerStyle={styles.detailContent}>
        <Pressable style={styles.backRow} onPress={() => setSelectedId(null)}>
          <Text style={styles.backText}>‹ Back to situations</Text>
        </Pressable>
        {error ? <Text style={styles.error}>{error}</Text> : null}

        <TextInput
          style={styles.titleInput}
          value={selectedDecision.title}
          onChangeText={(t) => updateDetail({ title: t })}
          placeholder="Situation title…"
          placeholderTextColor={c.slate}
        />

        <View style={styles.decidedRow}>
          <Switch
            value={selectedDecision.decided}
            onValueChange={(v) => updateDetail({ decided: v })}
            trackColor={{ true: t.goldGlow, false: t.navyLine }}
            thumbColor={selectedDecision.decided ? t.gold : t.ivory}
          />
          <Text style={styles.decidedLabel}>Decided</Text>
        </View>

        {/* Section toggles, mirroring the web detail page's four switches. */}
        <View style={styles.sectionTogglesRow}>
          <View style={styles.sectionToggleItem}>
            <Switch
              value={showSlider}
              onValueChange={setShowSlider}
              trackColor={{ true: t.goldGlow, false: t.navyLine }}
              thumbColor={showSlider ? t.gold : t.ivory}
            />
            <Text style={styles.sectionToggleLabel}>Decision Slider</Text>
          </View>
          <View style={styles.sectionToggleItem}>
            <Switch
              value={showMatrix}
              onValueChange={setShowMatrix}
              trackColor={{ true: t.goldGlow, false: t.navyLine }}
              thumbColor={showMatrix ? t.gold : t.ivory}
            />
            <Text style={styles.sectionToggleLabel}>Decision Matrix</Text>
          </View>
          <View style={styles.sectionToggleItem}>
            <Switch
              value={showChoiceTree}
              onValueChange={setShowChoiceTree}
              trackColor={{ true: t.goldGlow, false: t.navyLine }}
              thumbColor={showChoiceTree ? t.gold : t.ivory}
            />
            <Text style={styles.sectionToggleLabel}>Choice S.C.O.R.E</Text>
          </View>
          <View style={styles.sectionToggleItem}>
            <Switch
              value={showNotes}
              onValueChange={setShowNotes}
              trackColor={{ true: t.goldGlow, false: t.navyLine }}
              thumbColor={showNotes ? t.gold : t.ivory}
            />
            <Text style={styles.sectionToggleLabel}>Notes</Text>
          </View>
        </View>

        {/* Decision Slider (Time × Reversibility matrix) */}
        {showSlider ? (
          <DecisionSliderWidget
            key={`slider-${selectedId}`}
            value={tree.slider ?? DEFAULT_SLIDER_DATA}
            onChange={updateSlider}
          />
        ) : null}

        {/* Weighted Decision Matrix */}
        {showMatrix ? (
          <DecisionMatrixWidget
            key={`matrix-${selectedId}`}
            value={tree.matrix ?? DEFAULT_MATRIX_DATA}
            onChange={updateMatrix}
          />
        ) : null}

        {/* Choice S.C.O.R.E (choice tree) */}
        {showChoiceTree ? (
          <View style={styles.widgetCard}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <Text style={styles.sectionLabel}>Choice S.C.O.R.E</Text>
            <Text style={styles.sectionHint}>
              Tap “+ Add Option” below · tap ⋯ on an option for more actions
            </Text>

            <View style={styles.stepRow}>
              <TextInput
                style={styles.stepTitleInput}
                value={tree.stepTitle}
                onChangeText={(t) => updateTree((tr) => ({ ...tr, stepTitle: t }))}
                placeholder="Situation"
                placeholderTextColor={c.inputPlaceholderOnFill}
              />
              <TextInput
                style={styles.stepDescInput}
                value={tree.stepDescription}
                onChangeText={(t) => updateTree((tr) => ({ ...tr, stepDescription: t }))}
                placeholder="Description"
                placeholderTextColor={c.slate}
              />
            </View>

            <View style={styles.connectorWrap}>
              <View style={styles.connector} />
            </View>

            <View style={styles.choiceRow}>
              <TextInput
                style={styles.choiceTitleInput}
                value={tree.choiceTitle}
                onChangeText={(t) => updateTree((tr) => ({ ...tr, choiceTitle: t }))}
                placeholder="Choice"
                placeholderTextColor={c.text}
              />
            </View>

            <Pressable style={styles.addOptionButton} onPress={addOption}>
              <Text style={styles.addOptionText}>+ Add Option</Text>
            </Pressable>

            {tree.options.length === 0 ? (
              <Text style={styles.empty}>No options yet.</Text>
            ) : (
              <View style={styles.optionsRow}>
                <View style={styles.optionsColumn}>
                  {leftOptions.length === 0 ? (
                    <Text style={styles.columnEmpty}>Left</Text>
                  ) : (
                    leftOptions.map(renderOption)
                  )}
                </View>
                <View style={styles.columnDivider} />
                <View style={styles.optionsColumn}>
                  {rightOptions.length === 0 ? (
                    <Text style={styles.columnEmpty}>Right</Text>
                  ) : (
                    rightOptions.map(renderOption)
                  )}
                </View>
              </View>
            )}
          </View>
        ) : null}

        {/* Notes */}
        {showNotes ? (
          <View style={styles.widgetCard}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <Text style={styles.sectionLabel}>Notes</Text>
            <TextInput
              style={styles.notesInput}
              value={selectedDecision.notes ?? ''}
              onChangeText={(t) => updateDetail({ notes: t })}
              placeholder="Write your notes here…"
              placeholderTextColor={c.slate}
              multiline
              textAlignVertical="top"
            />
          </View>
        ) : null}
      </ScrollView>
    );
  }

  // ----- List view: active decisions + collapsible archive -----
  const activeDecisions = decisions.filter((d) => !d.decided);
  const archivedDecisions = decisions.filter((d) => d.decided);

  return (
    <ScrollView style={styles.root} contentContainerStyle={styles.listContent}>
      <View style={styles.headerRow}>
        <View>
          <Text style={styles.heading}>J.N.U.S</Text>
          <Text style={styles.subheading}>Decision support tools</Text>
        </View>
        <Pressable
          style={[styles.addButton, busy && styles.addButtonDisabled]}
          onPress={createDecision}
          disabled={busy}
        >
          <Text style={styles.addText}>+ New Situation</Text>
        </Pressable>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      {activeDecisions.length === 0 && archivedDecisions.length === 0 ? (
        <View style={styles.emptyBox}>
          <Text style={styles.empty}>No situations yet.</Text>
          <Text style={styles.emptyHint}>Tap “+ New Situation” to get started.</Text>
        </View>
      ) : null}

      {activeDecisions.length > 0 ? (
        <View style={styles.list}>{activeDecisions.map(renderCard)}</View>
      ) : null}

      {archivedDecisions.length > 0 ? (
        <View style={styles.archiveSection}>
          <Pressable style={styles.archiveToggle} onPress={() => setShowArchive((v) => !v)}>
            <Text style={styles.archiveToggleText}>
              {showArchive ? '▾' : '▸'} Archive ({archivedDecisions.length})
            </Text>
          </Pressable>
          {showArchive ? <View style={styles.list}>{archivedDecisions.map(renderCard)}</View> : null}
        </View>
      ) : null}

      {activeDecisions.length > 0 || archivedDecisions.length > 0 ? (
        <Text style={styles.hint}>Tap to open · long-press for options</Text>
      ) : null}
    </ScrollView>
  );
}

const useStyles = makeThemedStyles(({ t, c, radii }) =>
  StyleSheet.create({
  root: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  error: { color: c.danger, marginBottom: 8, fontSize: 13 },

  // ----- List view -----
  listContent: { padding: 12, paddingBottom: 24 },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 14,
    gap: 10,
  },
  heading: { fontFamily: DISPLAY_FONT, fontSize: 20, color: t.ivory, letterSpacing: 2.5, textTransform: 'uppercase' },
  subheading: { fontSize: 12, color: c.slate, marginTop: 2 },
  addButton: {
    backgroundColor: c.lime,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: t.gold,
  },
  addButtonDisabled: { opacity: 0.5 },
  addText: { fontWeight: '900', color: t.goldBright, fontSize: 12 },
  emptyBox: { alignItems: 'center', paddingVertical: 32 },
  empty: { color: c.slate, fontStyle: 'italic', textAlign: 'center', fontSize: 13 },
  emptyHint: { color: c.slate, fontSize: 12, marginTop: 4, textAlign: 'center' },
  list: { gap: 8 },
  card: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.glass,
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  cardTopRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  cardTitle: { flex: 1, fontSize: 15, fontWeight: '700', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  cardDate: { fontSize: 11, color: c.slate, marginTop: 3 },
  cardNotes: { fontSize: 12, color: c.slate, marginTop: 3 },
  decidedBadge: {
    backgroundColor: c.lime,
    borderRadius: radii.pill,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  decidedBadgeText: { fontFamily: DISPLAY_FONT,
    fontSize: 9,
    fontWeight: '800',
    color: t.goldBright,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  archiveSection: { marginTop: 16 },
  archiveToggle: { marginBottom: 8 },
  archiveToggleText: { fontSize: 13, fontWeight: '700', color: c.slate },
  hint: { fontSize: 11, color: c.slate, textAlign: 'center', marginTop: 14 },

  // ----- Detail view -----
  detailContent: { padding: 12, paddingBottom: 32 },
  backRow: { marginBottom: 10 },
  backText: { color: t.goldBright, fontWeight: '700', fontSize: 14 },
  titleInput: {
    fontSize: 19,
    fontWeight: '900',
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  decidedRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  decidedLabel: { fontSize: 14, fontWeight: '700', color: c.text },
  sectionTogglesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 18,
  },
  sectionToggleItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  sectionToggleLabel: { fontSize: 11, color: c.slate, fontWeight: '600' },

  // ----- Decision widgets (shared card wrapper) -----
  widgetCard: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.glass,
    borderRadius: 22,
    padding: 14,
    marginBottom: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  widgetTitle: { fontSize: 15, fontWeight: '800', color: c.text, textAlign: 'center', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  widgetSubtitle: {
    fontSize: 9,
    color: c.slate,
    marginTop: 2,
    marginBottom: 8,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  widgetBox: {
    width: '100%',
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 8,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  widgetBoxLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  widgetBoxText: { fontSize: 12, lineHeight: 17, color: c.text },

  // ----- Decision Slider -----
  quadrantBadge: {
    borderRadius: radii.pill,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 3,
    marginBottom: 10,
  },
  quadrantBadgeText: { fontFamily: DISPLAY_FONT, fontSize: 9, fontWeight: '800', letterSpacing: 1.5, textTransform: 'uppercase' },
  sliderAxisLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 9,
    color: c.slate,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginVertical: 2,
  },
  sliderGrid: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    borderRadius: 14,
    backgroundColor: t.navy,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  sliderRow: { flexDirection: 'row', height: SLIDER_CELL },
  sliderCell: {
    width: SLIDER_CELL,
    height: SLIDER_CELL,
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  sliderDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  sliderXAxisRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  sliderAxisLabelSmall: { fontFamily: DISPLAY_FONT,
    fontSize: 8,
    color: c.slate,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },

  // ----- Decision Matrix Table -----
  matrixControlsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'center',
    marginBottom: 8,
  },
  matrixToggleGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: t.glass,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  matrixControlLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 9,
    fontWeight: '700',
    color: c.slate,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  matrixToggleBtn: {
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 3,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  matrixToggleBtnText: { fontSize: 10, fontWeight: '700', color: c.text },
  matrixToggleBtnTextActive: { color: t.onGold },
  matrixBudgetGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: t.glass,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  matrixBudgetInput: {
    width: 34,
    fontSize: 11,
    paddingVertical: 2,
    backgroundColor: c.inputFill,
    borderRadius: 6,
    textAlign: 'center',
    color: c.text,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  matrixTotalText: { fontSize: 10, fontWeight: '700', letterSpacing: 0.5 },
  matrixActionsRow: { flexDirection: 'row', gap: 8, marginBottom: 10 },
  matrixActionBtn: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  matrixActionText: { fontSize: 10, fontWeight: '700', color: c.text },
  matrixTableScroll: { width: '100%', borderRadius: 14, backgroundColor: t.navy, borderWidth: 1, borderColor: t.navyLine },
  matrixRow: { flexDirection: 'row', borderBottomWidth: 1, borderColor: t.lineFaint },
  matrixFooterRow: { borderBottomWidth: 0, borderTopWidth: 2, borderColor: t.lineFaint },
  matrixCell: {
    paddingHorizontal: 6,
    paddingVertical: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  matrixCellBordered: { borderLeftWidth: 1, borderColor: t.lineFaint },
  matrixHeaderText: { fontFamily: DISPLAY_FONT,
    fontSize: 9,
    fontWeight: '700',
    color: c.slate,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  matrixOptionNameInput: {
    width: 76,
    fontSize: 10,
    fontWeight: '700',
    color: c.text,
    textAlign: 'center',
    backgroundColor: c.inputFill,
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  matrixRemoveBtn: { paddingHorizontal: 4 },
  matrixRemoveText: { fontSize: 12, fontWeight: '900', color: c.slate, lineHeight: 12 },
  matrixCriteriaNameRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  matrixCriteriaNameInput: {
    fontSize: 11,
    color: c.text,
    width: 84,
    backgroundColor: c.inputFill,
    borderRadius: 6,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  matrixWeightInput: {
    width: 32,
    fontSize: 11,
    fontWeight: '800',
    color: t.goldBright,
    textAlign: 'center',
    backgroundColor: c.inputFill,
    borderRadius: 4,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  matrixScoreInput: {
    width: 28,
    fontSize: 11,
    color: c.text,
    textAlign: 'center',
    backgroundColor: c.inputFill,
    borderRadius: 4,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  matrixWeightedText: { fontSize: 9, color: c.slate, marginLeft: 4 },
  matrixWinnerBg: { backgroundColor: t.goldGlow },
  matrixWinnerBgSoft: { backgroundColor: c.limeSoft },
  matrixTotalLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 10,
    fontWeight: '800',
    color: c.slate,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  matrixTotalValue: { fontSize: 13, fontWeight: '800', color: c.text },
  matrixWinnerLabel: { fontSize: 8, color: t.goldBright, fontWeight: '700', letterSpacing: 0.5, marginTop: 1 },
  sectionLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 12,
    fontWeight: '800',
    color: c.slate,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionHint: {
    fontSize: 10,
    color: c.slate,
    marginBottom: 10,
    fontStyle: 'italic',
  },
  notesInput: {
    minHeight: 120,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: c.text,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  stepRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  stepTitleInput: {
    width: 92,
    height: 34,
    borderRadius: 14,
    backgroundColor: t.gold,
    color: t.onGold,
    fontWeight: '800',
    fontSize: 12,
    textAlign: 'center',
  },
  stepDescInput: {
    flex: 1,
    height: 34,
    borderRadius: 14,
    backgroundColor: c.inputFill,
    paddingHorizontal: 10,
    fontSize: 12,
    color: c.text,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  connectorWrap: { alignItems: 'center', marginVertical: 4 },
  connector: { width: 3, height: 18, borderRadius: 2, backgroundColor: t.goldBright },
  choiceRow: { alignItems: 'center', marginBottom: 12 },
  choiceTitleInput: {
    width: 110,
    height: 34,
    borderRadius: 14,
    backgroundColor: t.gold,
    color: t.onGold,
    fontWeight: '800',
    fontSize: 13,
    textAlign: 'center',
  },
  addOptionButton: {
    boxShadow: `0 6px 18px ${t.goldGlow}`,
    alignSelf: 'center',
    backgroundColor: t.gold,
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
    marginBottom: 14,
  },
  addOptionText: { color: t.onGold, fontWeight: '800', fontSize: 12 },
  optionsRow: { flexDirection: 'row', gap: 8 },
  optionsColumn: { flex: 1, gap: 10 },
  columnEmpty: {
    fontSize: 11,
    color: c.slate,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 8,
  },
  columnDivider: { width: 1, backgroundColor: t.lineFaint },
  optionCard: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.glass,
    borderRadius: 14,
    padding: 8,
    gap: 6,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  optionHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  optionTitleInput: {
    flex: 1,
    height: 30,
    borderRadius: 14,
    backgroundColor: c.inputFill,
    paddingHorizontal: 8,
    fontSize: 12,
    fontWeight: '700',
    color: c.text,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  optionMenuButton: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  optionMenuText: { fontSize: 14, fontWeight: '900', color: c.slate },
  tagRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  tag: {
    fontSize: 9,
    fontWeight: '800',
    color: '#ffffff',
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 3,
    overflow: 'hidden',
  },
  tagReward: { backgroundColor: t.onPace },
  tagExposure: { backgroundColor: t.lapsed },
  tagInput: {
    flex: 1,
    height: 26,
    borderRadius: 14,
    backgroundColor: c.inputFill,
    paddingHorizontal: 6,
    fontSize: 11,
    color: c.text,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  tagRemove: { fontSize: 15, fontWeight: '900', color: c.slate, paddingHorizontal: 2 },
  }),
);
